import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { SectionHeader } from '@plato/ui-legacy/components/typography/section-header';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import { parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { resolveActiveMetric } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { DataStatBlock } from '../../dashboard/components/data-stat-block.tsx';

type ExplorerViewRevenueStatsProps = {
    className?: string;
};

/** One year tile: the figure, plus the window it covers when the year label doesn't say it. */
type YearTile = {
    year: number;
    totalAmount: number;
    window?: string;
};

export function ExplorerViewRevenueStats({ className }: ExplorerViewRevenueStatsProps) {
    const { t } = useTranslation('explorer');
    const locale = useResolvedLocale();
    const params = useParams();
    const erpCustomerId = params.customer_id || '';
    const appConfig = useAppConfig();
    const { defaultTimeframe } = appConfig;

    const { metric, billingStatus } = resolveActiveMetric(appConfig);
    const isTrailingTwelveMonths = defaultTimeframe === 'TTM';

    const queryParams = useMemo(
        () => ({
            filters: {
                customer: [erpCustomerId],
                clerk: [],
                industry: [],
                product: [],
                productGroup: [],
                storageLocation: [],
                supplier: [],
                salesRep: [],
                normalisedCategory: [],
            },
            billingStatus,
        }),
        [erpCustomerId, billingStatus],
    );

    const { data: tileData = [], isLoading: renderIsLoading } =
        api.platoUser.query.explorer.getRevenueStats.getByYearRangeForWindow.useQuery({
            ...queryParams,
            timeframe: defaultTimeframe,
        });

    // `dayFormat` renders a cut-off ("28 Aug"), `dayYearFormat` a rolling window that crosses
    // a year boundary ("8 Sep 25 – 8 Sep 26") — a TTM window starts on a day, not on the 1st,
    // so it needs the day. `formatRange` picks the locale's own separator and abbreviations.
    const { dayFormat, dayYearFormat } = useMemo(
        () => ({
            dayFormat: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }),
            dayYearFormat: new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'short',
                year: '2-digit',
            }),
        }),
        [locale],
    );

    const tiles: YearTile[] = tileData.map(({ year, total_amount, from, to }) => ({
        year,
        totalAmount: total_amount,
        // A YTD window runs Jan 1 to the cut-off, which the header already spells out —
        // only a TTM window needs saying per tile
        window: isTrailingTwelveMonths ? dayYearFormat.formatRange(parseISO(from), parseISO(to)) : undefined,
    }));

    // The years are known before the query resolves, so the tiles keep their shape while
    // loading and only the figures arrive late.
    const placeholderTiles: YearTile[] = useMemo(() => {
        const currentYear = new Date().getFullYear();

        return Array.from({ length: 4 }, (_, index) => ({ year: currentYear - index, totalAmount: 0 }));
    }, []);

    const title = metric === 'Order_Sum' ? t('revenue_stats__title_order_sum') : t('revenue_stats__title');

    const currentYearWindow = tileData[0];
    let windowLabel: string | undefined;

    if (currentYearWindow) {
        windowLabel = isTrailingTwelveMonths
            ? t('revenue_stats__sub_title_ttm', {
                  date: dayFormat.format(parseISO(currentYearWindow.to)),
              })
            : t('revenue_stats__sub_title_ytd', {
                  range: dayFormat.formatRange(
                      parseISO(currentYearWindow.from),
                      parseISO(currentYearWindow.to),
                  ),
              });
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col">
                <SectionHeader title={title} />
                {windowLabel && <Typography variant="caption">{windowLabel}</Typography>}
            </div>
            <div className={cn('flex h-full w-full flex-row gap-3', className)}>
                <DataStatBlock className="grid w-full grid-cols-2 md:grid-cols-4">
                    {(tiles.length > 0 ? tiles : placeholderTiles).map((tile) => (
                        <DataStatBlock.Item key={tile.year}>
                            <DataStatBlock.Title className="pb-0">{tile.year}</DataStatBlock.Title>
                            {tile.window && <Typography variant="caption">{tile.window}</Typography>}
                            <DataStatBlock.Body isLoading={renderIsLoading}>
                                <CurrencyOrEmpty showEmptyIfZero>{tile.totalAmount}</CurrencyOrEmpty>
                            </DataStatBlock.Body>
                        </DataStatBlock.Item>
                    ))}
                </DataStatBlock>
            </div>
        </div>
    );
}
